// Reports, built from the reporting views and the ledger tables. Every query runs under the
// caller's row-level security: a department treasurer asking for a church-wide figure simply gets
// their own department back. Money is ALWAYS integer ngwee here; formatting is the screen's job.
import { supabase } from '@/lib/supabase'
import { unwrap } from './errors'
import { getPosition } from './remittances'
import type { Position } from './remittances'
import { groupSum, total } from '@/lib/reportMath'
import { fetchAllRows } from './paging'
import { monthRange, quarterId } from '@/lib/periods'

type Rows<T> = T[]
const q = <T>(res: unknown, msg: string) => unwrap(res as never, msg) as Rows<T>

export interface QuarterReport {
  departmentId: string
  quarterId: string
  income: { totalNgwee: number; byMonth: { month: string; ngwee: number }[]; byKind: { kind: string; ngwee: number }[] }
  /** Only when the quarter has an approved budget. */
  budget: {
    budgetedNgwee: number
    actualNgwee: number
    committedNgwee: number
    lines: { description: string; categoryName: string; budgetedNgwee: number; actualNgwee: number; committedNgwee: number; remainingNgwee: number }[]
  } | null
  spending: { paidNgwee: number; unbudgetedNgwee: number; waitingNgwee: number; byCategory: { category: string; ngwee: number }[] }
  remittances: {
    declaredNgwee: number
    confirmedNgwee: number
    items: { movedOn: string; method: string; declaredNgwee: number; confirmedNgwee: number; status: string }[]
  }
  position: Position | null
  /** What members owe right now (not tied to the quarter). */
  arrears: { members: number; totalNgwee: number }
}

export async function getQuarterReport(departmentId: string, quarterId: string): Promise<QuarterReport> {
  const [contrib, lines, paid, waiting, moves, recon, pos, arrears] = await Promise.all([
    fetchAllRows((f, t) => supabase.from('contribution').select('id, kind, period_month, amount_ngwee').eq('department_id', departmentId).eq('quarter_id', quarterId).in('status', ['posted', 'reversed']).order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('v_budget_line_actuals').select('budget_line_id, description, category_name, budgeted_ngwee, actual_ngwee, committed_ngwee, remaining_ngwee').eq('department_id', departmentId).eq('quarter_id', quarterId).eq('budget_status', 'approved').order('category_name').order('description').order('budget_line_id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('expense').select('id, amount_ngwee, is_unbudgeted, category:expense_category(name)').eq('department_id', departmentId).eq('quarter_id', quarterId).in('status', ['paid', 'void']).order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('expense').select('id, amount_ngwee').eq('department_id', departmentId).eq('quarter_id', quarterId).in('status', ['submitted', 'approved']).order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('cash_movement').select('id, moved_on, method, declared_amount_ngwee, status').eq('department_id', departmentId).eq('direction', 'dept_to_central').eq('quarter_id', quarterId).neq('status', 'cancelled').order('moved_on').order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('v_remittance_reconciliation').select('movement_id, matched_ngwee').eq('department_id', departmentId).eq('quarter_id', quarterId).order('movement_id').range(f, t)),
    getPosition(departmentId, quarterId),
    fetchAllRows((f, t) => supabase.from('v_member_arrears').select('member_id, arrears_ngwee').eq('department_id', departmentId).order('member_id').range(f, t)),
  ])

  const c = q<{ kind: string; period_month: string; amount_ngwee: number }>(contrib, 'Could not load the income.')
  const incomeRows = c.map((r) => ({ kind: r.kind, month: r.period_month.slice(0, 7), n: Number(r.amount_ngwee) }))

  const l = q<{ description: string; category_name: string; budgeted_ngwee: number; actual_ngwee: number; committed_ngwee: number; remaining_ngwee: number }>(lines, 'Could not load the budget.')
  const budgetLines = l.map((r) => ({
    description: r.description,
    categoryName: r.category_name,
    budgetedNgwee: Number(r.budgeted_ngwee),
    actualNgwee: Number(r.actual_ngwee),
    committedNgwee: Number(r.committed_ngwee),
    remainingNgwee: Number(r.remaining_ngwee),
  }))

  const p = q<{ amount_ngwee: number; is_unbudgeted: boolean; category: { name: string } | null }>(paid, 'Could not load the spending.')
  const w = q<{ amount_ngwee: number }>(waiting, 'Could not load the spending.')

  const m = q<{ id: string; moved_on: string; method: string; declared_amount_ngwee: number; status: string }>(moves, 'Could not load the remittances.')
  const matched = new Map(q<{ movement_id: string; matched_ngwee: number }>(recon, 'Could not load the remittances.').map((r) => [r.movement_id, Number(r.matched_ngwee)]))

  const a = q<{ arrears_ngwee: number }>(arrears, 'Could not load the arrears.')

  return {
    departmentId,
    quarterId,
    income: {
      totalNgwee: total(incomeRows.map((r) => r.n)),
      byMonth: groupSum(incomeRows, (r) => r.month, (r) => r.n).map((x) => ({ month: x.key, ngwee: x.ngwee })).sort((x, y) => x.month.localeCompare(y.month)),
      byKind: groupSum(incomeRows, (r) => r.kind, (r) => r.n).map((x) => ({ kind: x.key, ngwee: x.ngwee })),
    },
    budget: budgetLines.length === 0 ? null : {
      budgetedNgwee: total(budgetLines.map((x) => x.budgetedNgwee)),
      actualNgwee: total(budgetLines.map((x) => x.actualNgwee)),
      committedNgwee: total(budgetLines.map((x) => x.committedNgwee)),
      lines: budgetLines,
    },
    spending: {
      paidNgwee: total(p.map((x) => Number(x.amount_ngwee))),
      unbudgetedNgwee: total(p.filter((x) => x.is_unbudgeted).map((x) => Number(x.amount_ngwee))),
      waitingNgwee: total(w.map((x) => Number(x.amount_ngwee))),
      byCategory: groupSum(p, (x) => x.category?.name ?? 'Other', (x) => Number(x.amount_ngwee)).map((x) => ({ category: x.key, ngwee: x.ngwee })).sort((x, y) => y.ngwee - x.ngwee),
    },
    remittances: {
      declaredNgwee: total(m.map((x) => Number(x.declared_amount_ngwee))),
      confirmedNgwee: total(m.map((x) => matched.get(x.id) ?? 0)),
      items: m.map((x) => ({ movedOn: x.moved_on, method: x.method, declaredNgwee: Number(x.declared_amount_ngwee), confirmedNgwee: matched.get(x.id) ?? 0, status: x.status })),
    },
    position: pos,
    arrears: { members: a.length, totalNgwee: total(a.map((x) => Number(x.arrears_ngwee))) },
  }
}

// ---------------------------------------------------------------- church-wide
export interface DepartmentSummary {
  departmentId: string
  name: string
  collectedNgwee: number
  spentNgwee: number
  sentNgwee: number
  confirmedNgwee: number
  /** Declared but not yet confirmed by central treasury. */
  unconfirmedNgwee: number
  budgetedNgwee: number
  budgetActualNgwee: number
  cashOnHandNgwee: number
  arrearsNgwee: number
}

/** One row per department for a quarter (central treasury and auditors see all; others only theirs). */
export async function getChurchSummary(quarterId: string, departments: { id: string; name: string }[]): Promise<DepartmentSummary[]> {
  const [pos, recon, lines, arrears] = await Promise.all([
    supabase.from('v_department_position').select('department_id, collected_ngwee, remitted_ngwee, spent_local_ngwee, spent_central_ngwee, cash_on_hand_ngwee').eq('quarter_id', quarterId),
    fetchAllRows((f, t) => supabase.from('v_remittance_reconciliation').select('movement_id, department_id, matched_ngwee').eq('quarter_id', quarterId).eq('direction', 'dept_to_central').order('movement_id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('v_budget_line_actuals').select('budget_line_id, department_id, budgeted_ngwee, actual_ngwee').eq('quarter_id', quarterId).eq('budget_status', 'approved').order('budget_line_id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('v_member_arrears').select('department_id, member_id, arrears_ngwee').order('department_id').order('member_id').range(f, t)),
  ])
  const sumBy = <T extends { department_id: string }>(rows: T[], f: (r: T) => number) => {
    const out = new Map<string, number>()
    for (const r of rows) out.set(r.department_id, (out.get(r.department_id) ?? 0) + f(r))
    return out
  }
  const P = q<{ department_id: string; collected_ngwee: number; remitted_ngwee: number; spent_local_ngwee: number; spent_central_ngwee: number; cash_on_hand_ngwee: number }>(pos, 'Could not load the cash positions.')
  const R = sumBy(q<{ department_id: string; matched_ngwee: number }>(recon, 'Could not load the remittances.'), (r) => Number(r.matched_ngwee))
  const L = q<{ department_id: string; budgeted_ngwee: number; actual_ngwee: number }>(lines, 'Could not load the budgets.')
  const budgeted = sumBy(L, (r) => Number(r.budgeted_ngwee))
  const actual = sumBy(L, (r) => Number(r.actual_ngwee))
  const owed = sumBy(q<{ department_id: string; arrears_ngwee: number }>(arrears, 'Could not load the arrears.'), (r) => Number(r.arrears_ngwee))
  const byDept = new Map(P.map((r) => [r.department_id, r]))

  return departments
    .filter((d) => byDept.has(d.id))
    .map((d) => {
      const r = byDept.get(d.id)!
      const sent = Number(r.remitted_ngwee)
      const confirmed = R.get(d.id) ?? 0
      return {
        departmentId: d.id,
        name: d.name,
        collectedNgwee: Number(r.collected_ngwee),
        spentNgwee: Number(r.spent_local_ngwee) + Number(r.spent_central_ngwee),
        sentNgwee: sent,
        confirmedNgwee: confirmed,
        unconfirmedNgwee: sent - confirmed,
        budgetedNgwee: budgeted.get(d.id) ?? 0,
        budgetActualNgwee: actual.get(d.id) ?? 0,
        cashOnHandNgwee: Number(r.cash_on_hand_ngwee),
        arrearsNgwee: owed.get(d.id) ?? 0,
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

// ---------------------------------------------------------------- monthly
// The same rules as the quarterly report, cut to one calendar month, so the three months of a quarter
// add up to the quarter. Money in and spending belong to the month of their own date (the stored
// `period_month`); a remittance belongs to the month it was handed over. Budgets are set per quarter and
// the database keeps cash per quarter, so a month has no budget section and no opening or closing balance:
// it shows what came in, what went out, and the net change in the department's cash.

export interface MonthStatement {
  departmentId: string
  monthId: string
  income: { totalNgwee: number; byKind: { kind: string; ngwee: number }[] }
  spending: { paidNgwee: number; unbudgetedNgwee: number; waitingNgwee: number; byCategory: { category: string; ngwee: number }[] }
  remittances: {
    declaredNgwee: number
    confirmedNgwee: number
    items: { movedOn: string; method: string; declaredNgwee: number; confirmedNgwee: number; status: string }[]
  }
  /** What moved in the department's own cash this month: collected, less spent from it and sent on, plus anything central sent back. */
  cash: { collectedNgwee: number; spentFromCashNgwee: number; sentNgwee: number; receivedFromCentralNgwee: number; changeNgwee: number }
  /** What members owe right now (not tied to the month). */
  arrears: { members: number; totalNgwee: number }
}

/** The first day of a month id ('2026-10' -> '2026-10-01'), the value `period_month` holds. */
const monthStart = (monthId: string) => monthRange(monthId).start

export async function getMonthStatement(departmentId: string, monthId: string): Promise<MonthStatement> {
  const { start, end } = monthRange(monthId)
  const quarter = quarterId(start)
  const [contrib, paid, waiting, moves, back, recon, arrears] = await Promise.all([
    fetchAllRows((f, t) => supabase.from('contribution').select('id, kind, amount_ngwee').eq('department_id', departmentId).eq('period_month', monthStart(monthId)).in('status', ['posted', 'reversed']).order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('expense').select('id, amount_ngwee, is_unbudgeted, paid_from, category:expense_category(name)').eq('department_id', departmentId).eq('period_month', monthStart(monthId)).in('status', ['paid', 'void']).order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('expense').select('id, amount_ngwee').eq('department_id', departmentId).eq('period_month', monthStart(monthId)).in('status', ['submitted', 'approved']).order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('cash_movement').select('id, moved_on, method, declared_amount_ngwee, status').eq('department_id', departmentId).eq('direction', 'dept_to_central').gte('moved_on', start).lte('moved_on', end).neq('status', 'cancelled').order('moved_on').order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('cash_movement').select('id, declared_amount_ngwee').eq('department_id', departmentId).eq('direction', 'central_to_dept').gte('moved_on', start).lte('moved_on', end).neq('status', 'cancelled').order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('v_remittance_reconciliation').select('movement_id, matched_ngwee').eq('department_id', departmentId).eq('quarter_id', quarter).order('movement_id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('v_member_arrears').select('member_id, arrears_ngwee').eq('department_id', departmentId).order('member_id').range(f, t)),
  ])

  const c = q<{ kind: string; amount_ngwee: number }>(contrib, 'Could not load the income.').map((r) => ({ kind: r.kind, n: Number(r.amount_ngwee) }))
  const p = q<{ amount_ngwee: number; is_unbudgeted: boolean; paid_from: string; category: { name: string } | null }>(paid, 'Could not load the spending.')
  const w = q<{ amount_ngwee: number }>(waiting, 'Could not load the spending.')
  const m = q<{ id: string; moved_on: string; method: string; declared_amount_ngwee: number; status: string }>(moves, 'Could not load the remittances.')
  const b = q<{ declared_amount_ngwee: number }>(back, 'Could not load the money received from central treasury.')
  const matched = new Map(q<{ movement_id: string; matched_ngwee: number }>(recon, 'Could not load the remittances.').map((r) => [r.movement_id, Number(r.matched_ngwee)]))
  const a = q<{ arrears_ngwee: number }>(arrears, 'Could not load the arrears.')

  const collected = total(c.map((x) => x.n))
  const spentFromCash = total(p.filter((x) => x.paid_from === 'department_cash').map((x) => Number(x.amount_ngwee)))
  const sent = total(m.map((x) => Number(x.declared_amount_ngwee)))
  const fromCentral = total(b.map((x) => Number(x.declared_amount_ngwee)))

  return {
    departmentId,
    monthId,
    income: {
      totalNgwee: collected,
      byKind: groupSum(c, (r) => r.kind, (r) => r.n).map((x) => ({ kind: x.key, ngwee: x.ngwee })),
    },
    spending: {
      paidNgwee: total(p.map((x) => Number(x.amount_ngwee))),
      unbudgetedNgwee: total(p.filter((x) => x.is_unbudgeted).map((x) => Number(x.amount_ngwee))),
      waitingNgwee: total(w.map((x) => Number(x.amount_ngwee))),
      byCategory: groupSum(p, (x) => x.category?.name ?? 'Other', (x) => Number(x.amount_ngwee)).map((x) => ({ category: x.key, ngwee: x.ngwee })).sort((x, y) => y.ngwee - x.ngwee),
    },
    remittances: {
      declaredNgwee: sent,
      confirmedNgwee: total(m.map((x) => matched.get(x.id) ?? 0)),
      items: m.map((x) => ({ movedOn: x.moved_on, method: x.method, declaredNgwee: Number(x.declared_amount_ngwee), confirmedNgwee: matched.get(x.id) ?? 0, status: x.status })),
    },
    cash: { collectedNgwee: collected, spentFromCashNgwee: spentFromCash, sentNgwee: sent, receivedFromCentralNgwee: fromCentral, changeNgwee: collected - sent + fromCentral - spentFromCash },
    arrears: { members: a.length, totalNgwee: total(a.map((x) => Number(x.arrears_ngwee))) },
  }
}

export interface DepartmentMonthSummary {
  departmentId: string
  name: string
  collectedNgwee: number
  spentNgwee: number
  sentNgwee: number
  confirmedNgwee: number
  /** Declared but not yet confirmed by central treasury. */
  unconfirmedNgwee: number
  /** Net change in the department's cash this month (see MonthStatement.cash). */
  cashChangeNgwee: number
  arrearsNgwee: number
}

/** One row per department for a month (central treasury and auditors see all; others only theirs). */
export async function getChurchMonthSummary(monthId: string, departments: { id: string; name: string }[]): Promise<DepartmentMonthSummary[]> {
  const { start, end } = monthRange(monthId)
  const quarter = quarterId(start)
  const [visible, contrib, paid, moves, back, recon, arrears] = await Promise.all([
    // Which departments the caller may see: the same rule the quarterly summary uses (a row exists only for those).
    supabase.from('v_department_position').select('department_id').eq('quarter_id', quarter),
    fetchAllRows((f, t) => supabase.from('contribution').select('id, department_id, amount_ngwee').eq('period_month', monthStart(monthId)).in('status', ['posted', 'reversed']).order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('expense').select('id, department_id, amount_ngwee, paid_from').eq('period_month', monthStart(monthId)).in('status', ['paid', 'void']).order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('cash_movement').select('id, department_id, declared_amount_ngwee').eq('direction', 'dept_to_central').gte('moved_on', start).lte('moved_on', end).neq('status', 'cancelled').order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('cash_movement').select('id, department_id, declared_amount_ngwee').eq('direction', 'central_to_dept').gte('moved_on', start).lte('moved_on', end).neq('status', 'cancelled').order('id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('v_remittance_reconciliation').select('movement_id, department_id, matched_ngwee').eq('quarter_id', quarter).eq('direction', 'dept_to_central').order('movement_id').range(f, t)),
    fetchAllRows((f, t) => supabase.from('v_member_arrears').select('department_id, member_id, arrears_ngwee').order('department_id').order('member_id').range(f, t)),
  ])
  const sumBy = <T extends { department_id: string }>(rows: T[], f: (r: T) => number) => {
    const out = new Map<string, number>()
    for (const r of rows) out.set(r.department_id, (out.get(r.department_id) ?? 0) + f(r))
    return out
  }
  const seen = new Set(q<{ department_id: string }>(visible, 'Could not load the departments.').map((r) => r.department_id))
  const collected = sumBy(q<{ department_id: string; amount_ngwee: number }>(contrib, 'Could not load the income.'), (r) => Number(r.amount_ngwee))
  const paidRows = q<{ department_id: string; amount_ngwee: number; paid_from: string }>(paid, 'Could not load the spending.')
  const spent = sumBy(paidRows, (r) => Number(r.amount_ngwee))
  const spentFromCash = sumBy(paidRows.filter((r) => r.paid_from === 'department_cash'), (r) => Number(r.amount_ngwee))
  const moveRows = q<{ id: string; department_id: string; declared_amount_ngwee: number }>(moves, 'Could not load the remittances.')
  const sent = sumBy(moveRows, (r) => Number(r.declared_amount_ngwee))
  const fromCentral = sumBy(q<{ department_id: string; declared_amount_ngwee: number }>(back, 'Could not load the money received from central treasury.'), (r) => Number(r.declared_amount_ngwee))
  // Only the remittances handed over in this month count as confirmed against this month.
  const thisMonth = new Set(moveRows.map((r) => r.id))
  const confirmed = sumBy(q<{ movement_id: string; department_id: string; matched_ngwee: number }>(recon, 'Could not load the remittances.').filter((r) => thisMonth.has(r.movement_id)), (r) => Number(r.matched_ngwee))
  const owed = sumBy(q<{ department_id: string; arrears_ngwee: number }>(arrears, 'Could not load the arrears.'), (r) => Number(r.arrears_ngwee))

  return departments
    .filter((d) => seen.has(d.id))
    .map((d) => {
      const s = sent.get(d.id) ?? 0
      const c = collected.get(d.id) ?? 0
      return {
        departmentId: d.id,
        name: d.name,
        collectedNgwee: c,
        spentNgwee: spent.get(d.id) ?? 0,
        sentNgwee: s,
        confirmedNgwee: confirmed.get(d.id) ?? 0,
        unconfirmedNgwee: s - (confirmed.get(d.id) ?? 0),
        cashChangeNgwee: c - s + (fromCentral.get(d.id) ?? 0) - (spentFromCash.get(d.id) ?? 0),
        arrearsNgwee: owed.get(d.id) ?? 0,
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}
